/**
 * performLogout — déconnexion complète, best-effort.
 *
 * @remarks
 * Séquence **ordonnée**, l'ordre est important :
 *  1. `POST /api/auth/logout` **pendant que l'access token est encore dans le
 *     store** — le request interceptor l'attache en `Authorization: Bearer`.
 *     Le serveur blackliste l'access token, révoque le refresh token et émet
 *     le `Set-Cookie` d'expiration du cookie HTTP-only (cf. SAAS-541). Le JS
 *     ne peut PAS supprimer ce cookie lui-même (`HttpOnly`).
 *  2. Clear du store en mémoire — **TOUJOURS**, même si l'appel réseau échoue
 *     (token expiré, réseau down). La déconnexion locale ne doit jamais être
 *     bloquée par le back.
 *
 * **Imports dynamiques** : même raison que `refresh.ts` — casser le cycle
 * `lib/http/client → lib/auth → features/auth` et respecter la frontière
 * `lib → rien` (un import statique de `app/` ou `features/` casserait
 * eslint-plugin-boundaries). C'est ce qui permet d'appeler `performLogout`
 * aussi bien depuis `app/` que depuis une `feature/`.
 *
 * La navigation (`/login`) reste à la charge de l'appelant, comme avant.
 *
 * @example
 * ```ts
 * void performLogout().finally(() => {
 *   void navigate('/login');
 * });
 * ```
 */
export async function performLogout(): Promise<void> {
  try {
    const { logout } = await import('@/features/auth/api/auth');
    await logout();
  } catch {
    // best-effort : on nettoie l'état local quoi qu'il arrive.
  } finally {
    const { useAuthStore } = await import('@/app/stores/use-auth-store');
    useAuthStore.getState().logout();
  }
}
